import React, { useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Stack,
  Button,
  Alert,
  Tooltip,
  useTheme,
  alpha,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  FormControl,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  KeyboardArrowUp as ArrowUpIcon,
  ViewCompact as CompactIcon,
  GroupWork as GroupIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import type {
  MedicalReservationRecord,
  MedicalReservationUpdateRequest,
  ActionStatus,
  BulkActionType,
  OutOfHoursDisplayRecord,
} from '../types/medicalReservation';
import { OutOfHoursReservationTable } from '../components/MedicalReservation/OutOfHoursReservationTable';
import { DetailModal } from '../components/MedicalReservation/DetailModal';
import { EditModal } from '../components/MedicalReservation/EditModal';
import { AssignModal } from '../components/MedicalReservation/AssignModal';
import { FilterDrawer } from '../components/MedicalReservation/FilterDrawer';
import { PasteConfirmDialog } from '../components/MedicalReservation/PasteConfirmDialog';
import { useOutOfHoursMedicalReservationList } from '../hooks/useOutOfHoursMedicalReservationList';
import { useNotification } from '../hooks/useNotification';
import { mapToOutOfHoursDisplayBulk } from '../utils/outOfHoursDataMapper';
import './ManagementList.css';

/**
 * 指定時間外診療予約データ一覧画面
 *
 * 意図：
 * - 指定時間外の診療予約レコードの一覧表示と管理
 * - 過去の診療開始時間のデータをグレイアウト表示
 * - 検索・フィルタリング・詳細表示機能
 *
 * 注意点：
 * - CSVアップロード機能は含まない
 * - 過去データの視覚的区別
 * - パフォーマンス最適化
 */
export const OutOfHoursMedicalReservationListPage: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const { showNotification } = useNotification();

  // CSVアップロード時に渡された指定時間外データを取得
  const outOfHoursUploadedData = location.state?.outOfHoursUploadedData || null;
  const uploadTimestamp = location.state?.uploadTimestamp;
  const uploadTimeRange = location.state?.timeRange;

  const {
    data: apiData,
    meta,
    isLoading,
    error,
    filters,
    selectedRecord,
    detailModalOpen,
    assignModalOpen,
    selectedIds,
    updateFilters,
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    handleSearch,
    refresh,
    showDetails,
    showAssignModal,
    closeModals,
    deleteRecord,
    assignRecord,
    clearError,
    toggleSelectRecord,
    selectAllRecords,
    clearSelection,
    pasteDialogOpen,
    pasteStatus,
    isProcessing,
    openPasteDialog,
    closePasteDialog,
    pasteSelectedRecords,
    isPastConsultation,
  } = useOutOfHoursMedicalReservationList();

  // UI状態管理
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const filterApplyRef = useRef<(() => void) | null>(null);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkActionType, setBulkActionType] = useState<BulkActionType>('paste');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<MedicalReservationRecord | null>(null);
  const [compactMode, setCompactMode] = useState(() => {
    const saved = localStorage.getItem('mederi_compact_mode');
    return saved === 'true';
  });

  // アップロードデータ表示フラグ
  const [showUploadedData, setShowUploadedData] = useState(!!outOfHoursUploadedData);

  // 表示データの決定（アップロードデータがある場合は優先表示）
  const displayData = (showUploadedData && outOfHoursUploadedData) ? outOfHoursUploadedData : apiData;

  // 未実装の状態管理（仮実装）
  const editModalOpen = false;
  const updateRecord = async (id: string, data: any) => ({} as MedicalReservationRecord);
  const closeDetailModal = closeModals;
  const closeEditModal = closeModals;
  const closeAssignModal = closeModals;

  // レコード詳細表示
  const handleViewDetails = useCallback((record: MedicalReservationRecord) => {
    showDetails(record);
  }, [showDetails]);

  // レコード貼り付け（個別レコードから）
  const handlePaste = useCallback((record: MedicalReservationRecord) => {
    // 選択状態をクリアして、このレコードのみを選択
    clearSelection();
    toggleSelectRecord(record.id);
    // 貼り付けダイアログを開く
    openPasteDialog();
  }, [clearSelection, toggleSelectRecord, openPasteDialog]);

  // レコード振り分け
  const handleAssign = useCallback((record: MedicalReservationRecord) => {
    showAssignModal(record);
  }, [showAssignModal]);

  // レコード削除確認
  const handleDeleteClick = useCallback((record: MedicalReservationRecord) => {
    setRecordToDelete(record);
    setDeleteConfirmOpen(true);
  }, []);

  // レコード削除実行
  const handleDeleteConfirm = useCallback(async () => {
    if (!recordToDelete) return;

    try {
      const success = await deleteRecord(recordToDelete.id);
      if (success) {
        showNotification('success', 'レコードを削除しました');
        setDeleteConfirmOpen(false);
        setRecordToDelete(null);
      }
    } catch (error) {
      showNotification('error', 'レコードの削除に失敗しました');
    }
  }, [recordToDelete, deleteRecord, showNotification]);

  // 削除キャンセル
  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmOpen(false);
    setRecordToDelete(null);
  }, []);

  // レコード更新
  const handleSaveRecord = useCallback(async (id: string, updateData: MedicalReservationUpdateRequest) => {
    try {
      const updatedRecord = await updateRecord(id, updateData);
      showNotification('success', 'レコードを更新しました');
      return updatedRecord;
    } catch (error) {
      showNotification('error', 'レコードの更新に失敗しました');
      throw error;
    }
  }, [updateRecord, showNotification]);

  // レコード振り分け実行
  const handleAssignRecord = useCallback(async (id: string, assignedTo: string, notes?: string) => {
    try {
      const success = await assignRecord(id, assignedTo, notes);
      if (success) {
        showNotification('success', `${assignedTo}に振り分けました`);
        return {} as MedicalReservationRecord;
      }
      throw new Error('振り分けに失敗しました');
    } catch (error) {
      showNotification('error', '振り分けに失敗しました');
      throw error;
    }
  }, [assignRecord, showNotification]);

  // 一括操作実行
  const handleBulkAction = useCallback(async () => {
    if (selectedIds.length === 0) {
      showNotification('warning', 'レコードを選択してください');
      return;
    }

    switch (bulkActionType) {
      case 'paste':
        // 一括操作ダイアログを閉じて貼り付けダイアログを開く
        setBulkActionDialogOpen(false);
        openPasteDialog();
        break;
      case 'delete':
        // 将来実装
        showNotification('info', 'この機能は開発中です');
        break;
      case 'notify':
        // 将来実装
        showNotification('info', 'この機能は開発中です');
        break;
    }
  }, [selectedIds, bulkActionType, openPasteDialog, showNotification]);

  // コンパクトモードの切り替え
  const handleCompactModeToggle = useCallback(() => {
    const newMode = !compactMode;
    setCompactMode(newMode);
    localStorage.setItem('mederi_compact_mode', String(newMode));
  }, [compactMode]);

  // モック担当医師オプション
  const doctorOptions = ['田中医師', '佐藤医師', '山田医師'];

  // タブと検索欄に表示済みの条件は件数に含めない。
  const getAdditionalFilterCount = () =>
    Number(Boolean(filters.doctor)) + Number(Boolean(filters.dateRange?.start || filters.dateRange?.end));

  // 過去のレコード数の計算
  const getPastRecordCount = () => {
    return displayData.filter((record: MedicalReservationRecord) => isPastConsultation(record.consultationStartTime)).length;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: theme.palette.background.default,
        py: 0,
        maxWidth: 'none',
        mx: 'auto',
      }}
    >
      <div>
        {/* ヘッダーセクション */}
        <Paper
          className="admin-page-heading"
          elevation={0}
          sx={{
            px: 0,
            py: 0,
            mb: 2,
            background: 'transparent',
            border: 'none',
            boxShadow: 'none',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
          >
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: compactMode ? 0 : 0.5 }}>
                <Typography
                  component="h1"
                  variant={compactMode ? "h5" : "h4"}
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                  }}
                >
                  指定時間外診療予約データ一覧
                </Typography>
              </Stack>
              {!compactMode && (
                <Typography variant="body2" color="text.secondary">
                  指定時間外の診療予約レコードの一覧表示・検索・管理
                </Typography>
              )}
              {meta && (
                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                  <Chip
                    label={`総レコード数: ${meta.total_records}`}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                  {getPastRecordCount() > 0 && (
                    <Chip
                      label={`過去の診療: ${getPastRecordCount()}件`}
                      size="small"
                      color="default"
                      variant="outlined"
                      sx={{
                        borderColor: theme.palette.grey[400],
                        color: theme.palette.grey[600]
                      }}
                    />
                  )}
                  {selectedIds.length > 0 && (
                    <Chip
                      label={`選択中: ${selectedIds.length}件`}
                      size="small"
                      color="secondary"
                    />
                  )}
                </Stack>
              )}
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" className="reservation-heading-actions">
              <Tooltip title="表示モード切替" placement="bottom">
                <IconButton
                  onClick={handleCompactModeToggle}
                  sx={{
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.warning.main, 0.2),
                    },
                  }}
                >
                  <CompactIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="データを更新" placement="bottom">
                <IconButton
                  onClick={refresh}
                  sx={{
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.warning.main, 0.2),
                    },
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>

        {/* 検索・フィルターバー */}
        <Box sx={{ px: 0, mb: 2 }}>
          <Paper
            className="reservation-search-panel"
            elevation={0}
            sx={{
              p: 0,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              background: theme.palette.background.paper,
            }}
          >
            <div className="management-tabs" role="tablist" aria-label="通知ステータス">
              {([
                { value: '', label: 'すべて' },
                { value: 'notification_required', label: '要通知' },
                { value: 'prescription_unavailable', label: '処方不可通知' },
                { value: 'none', label: '通知なし' },
              ] as const).map(tab => (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={tab.value === '' ? !filters.actionStatus?.length : filters.actionStatus?.length === 1 && filters.actionStatus[0] === tab.value}
                  onClick={() => updateFilters({ actionStatus: tab.value ? [tab.value] : [], page: 1 })}
                >{tab.label}</button>
              ))}
              {(filters.actionStatus?.length || 0) > 1 && <span className="reservation-multiple-status">複数条件</span>}
            </div>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" className="reservation-search-controls">
              <TextField
                placeholder="anybot ID、患者名、担当医師で検索..."
                value={filters.search || ''}
                onChange={(e) => updateFilters({ search: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: theme.palette.divider,
                    },
                  },
                }}
                sx={{ minWidth: 220, flexGrow: 1, maxWidth: 460 }}
              />
              <Button variant="contained" onClick={() => filterDrawerOpen ? filterApplyRef.current?.() : handleSearch(filters.search || '')}>検索</Button>
              <Button
                variant="outlined"
                startIcon={filterDrawerOpen ? <ArrowUpIcon /> : <FilterIcon />}
                onClick={() => setFilterDrawerOpen(!filterDrawerOpen)}
                aria-expanded={filterDrawerOpen}
                aria-controls="reservation-detail-filters"
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  borderColor: theme.palette.divider,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.warning.main, 0.05),
                  },
                }}
              >
                {filterDrawerOpen ? '詳細絞り込みを閉じる' : '詳細絞り込み'}
                {getAdditionalFilterCount() > 0 && (
                  <span className="management-filter-count">{getAdditionalFilterCount()}</span>
                )}
              </Button>
              {selectedIds.length > 0 && (
                <Button
                  variant="contained"
                  startIcon={<GroupIcon />}
                  onClick={() => setBulkActionDialogOpen(true)}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    bgcolor: theme.palette.warning.main,
                    '&:hover': {
                      bgcolor: theme.palette.warning.dark,
                    },
                  }}
                >
                  一括操作
                </Button>
              )}
            </Stack>

            <FilterDrawer
              presentation="inline"
              applyRef={filterApplyRef}
              open={filterDrawerOpen}
              onClose={() => setFilterDrawerOpen(false)}
              filters={filters}
              onFiltersChange={updateFilters}
              doctorOptions={doctorOptions}
            />

            {getAdditionalFilterCount() > 0 && (
              <div className="reservation-applied-filters" aria-label="適用中の詳細条件">
                {filters.doctor && (
                  <Chip
                    label={`担当医師: ${filters.doctor}`}
                    onDelete={() => updateFilters({ doctor: '' })}
                    size="small"
                  />
                )}
                {(filters.dateRange?.start || filters.dateRange?.end) && (
                  <Chip
                    label={`期間: ${filters.dateRange?.start || ''} - ${filters.dateRange?.end || ''}`}
                    onDelete={() => updateFilters({ dateRange: undefined })}
                    size="small"
                  />
                )}
              </div>
            )}
          </Paper>
        </Box>

        {/* アップロードデータ表示中の通知 */}
        {showUploadedData && outOfHoursUploadedData && (
          <Box sx={{ px: 0, mb: 2 }}>
            <Alert
              severity="info"
              sx={{ borderRadius: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setShowUploadedData(false)}
                >
                  最新データを取得
                </Button>
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                CSVアップロード時の指定時間外データを表示中（{outOfHoursUploadedData.length}件）
              </Typography>
              {uploadTimestamp && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  アップロード時刻: {new Date(uploadTimestamp).toLocaleString('ja-JP')}
                </Typography>
              )}
              {uploadTimeRange && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  指定時間帯: {uploadTimeRange.startDate} {uploadTimeRange.startTime} - {uploadTimeRange.endDate} {uploadTimeRange.endTime}
                </Typography>
              )}
            </Alert>
          </Box>
        )}

        {/* データテーブル */}
        <Box className="reservation-results-panel" sx={{ px: 0 }}>
          <div className="management-results-head"><h2>検索結果</h2><span>{showUploadedData ? displayData.length : meta?.total_records ?? displayData.length} 件の診療予約</span></div>
          {error ? (
            <Alert
              severity="error"
              sx={{ borderRadius: 2, whiteSpace: 'pre-wrap' }}
              action={
                <Button color="inherit" size="small" onClick={clearError}>
                  閉じる
                </Button>
              }
            >
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                データの取得に失敗しました
              </Typography>
              <Typography variant="body2" component="div">
                {error}
              </Typography>
              {process.env.NODE_ENV === 'development' && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace' }}>
                    デバッグ情報:
                    - エンドポイント: /api/medical-reservations/out-of-hours
                    - ベースURL: {process.env.REACT_APP_API_BASE_URL || '未設定'}
                    - トークン: {localStorage.getItem('mederi_auth_token') ? '設定済み' : '未設定'}
                  </Typography>
                </Box>
              )}
            </Alert>
          ) : (
            <OutOfHoursReservationTable
              data={mapToOutOfHoursDisplayBulk(displayData.map((record: MedicalReservationRecord) => ({
                ...record,
                // 過去のデータにグレイアウト用のフラグを追加
                isPast: isPastConsultation(record.consultationStartTime),
              })))}
              isLoading={isLoading}
              onViewDetails={handleViewDetails}
              onEdit={handlePaste}
              onAssign={handleAssign}
              onDelete={handleDeleteClick}
              filters={filters}
              onFiltersChange={updateFilters}
              meta={meta || undefined}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelectRecord}
              onSelectAll={selectAllRecords}
              onClearSelection={clearSelection}
              compactMode={compactMode}
            />
          )}
        </Box>
      </div>

      {/* フローティングアクションボタン */}
      {selectedIds.length > 0 && (
        <Fab
          color="warning"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
          }}
          onClick={() => setBulkActionDialogOpen(true)}
        >
          <GroupIcon />
        </Fab>
      )}

      {/* モーダル群 */}
      <DetailModal
        open={detailModalOpen}
        record={selectedRecord}
        onClose={closeDetailModal}
      />

      <EditModal
        open={editModalOpen}
        record={selectedRecord}
        onClose={closeEditModal}
        onSave={handleSaveRecord}
      />

      <AssignModal
        open={assignModalOpen}
        record={selectedRecord}
        onClose={closeAssignModal}
        onAssign={handleAssignRecord}
      />

      {/* 一括操作ダイアログ */}
      <Dialog
        open={bulkActionDialogOpen}
        onClose={() => setBulkActionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <GroupIcon />
            一括操作
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              選択された{selectedIds.length}件のレコードに対して一括操作を実行します。
            </Typography>

            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">操作タイプ</FormLabel>
              <RadioGroup
                value={bulkActionType}
                onChange={(e) => setBulkActionType(e.target.value as BulkActionType)}
              >
                <FormControlLabel
                  value="paste"
                  control={<Radio />}
                  label="スプレッドシートに貼り付け"
                />
                <FormControlLabel
                  value="delete"
                  control={<Radio />}
                  label="削除"
                  disabled
                />
                <FormControlLabel
                  value="notify"
                  control={<Radio />}
                  label="通知設定"
                  disabled
                />
              </RadioGroup>
            </FormControl>

            {bulkActionType === 'paste' && (
              <Alert severity="info">
                選択されたレコードをGoogleスプレッドシートに貼り付けます。
                貼り付け成功後、ステータスは自動的に「診療リスト登録済み」に更新されます。
              </Alert>
            )}

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkActionDialogOpen(false)}>
            キャンセル
          </Button>
          <Button
            onClick={handleBulkAction}
            variant="contained"
            disabled={false}
          >
            実行
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteIcon color="error" />
            レコードの削除確認
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1" gutterBottom>
              以下のレコードを削除しますか？
            </Typography>
            {recordToDelete && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>患者名:</strong> {recordToDelete.patientName}
                </Typography>
                <Typography variant="body2">
                  <strong>anybot ID:</strong> {recordToDelete.anybotId}
                </Typography>
                <Typography variant="body2">
                  <strong>担当医師:</strong> {recordToDelete.doctorInCharge}
                </Typography>
                <Typography variant="body2">
                  <strong>診療開始時間:</strong> {format(new Date(recordToDelete.consultationStartTime), 'yyyy/MM/dd HH:mm', { locale: ja })}
                  {isPastConsultation(recordToDelete.consultationStartTime) && (
                    <Chip label="過去" size="small" sx={{ ml: 1 }} />
                  )}
                </Typography>
              </Box>
            )}
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              ※ この操作は取り消すことができません。
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>
            キャンセル
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
          >
            削除する
          </Button>
        </DialogActions>
      </Dialog>


      {/* 貼り付け確認ダイアログ */}
      <PasteConfirmDialog
        open={pasteDialogOpen}
        selectedRecords={displayData.filter((record: MedicalReservationRecord) => selectedIds.includes(record.id))}
        onClose={closePasteDialog}
        onConfirm={pasteSelectedRecords}
        pasteStatus={pasteStatus}
        isProcessing={isProcessing}
      />
    </Box>
  );
};
